import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { get } from '@/lib/api';
import { trackPromoModalView, trackPromoModalClick, trackPromoModalClose } from '@/utils/analytics';

// Paths where the promo modal MUST NEVER appear.
// Using substring match (location.pathname.includes(p)) so locale-prefixed
// routes (e.g. /pt/book/manor, /es/checkin/...) are also covered.
const EXCLUDED_PATHS = [
  // ── Booking flow — NEVER interrupt a user picking dates or paying ──
  '/book',           // covers /book, /book/:houseId, /book/:houseId/checkout, /book/confirmation
  // ── Guest flows (post-booking) ──
  '/reservation',    // /reservation/:token
  '/checkin',        // /checkin/:token
  '/pre-checkin',
  // ── Promo & offer pages — showing a promo modal on top of a promo page is silly ──
  '/promotions',     // /promotions/:promoId
  '/offers',
  '/easter',
  // ── Admin / utility ──
  '/qapanel',
  '/unsubscribe',
  '/privacy-policy',
  '/terms-of-service',
  '/_stories',
  '/_feed',
];

// Persistent A/B variant assignment (stable per visitor across sessions)
const getOrAssignVariant = (promoId) => {
  if (typeof window === 'undefined') return 'A';
  const key = `promo_variant_${promoId}`;
  let v = localStorage.getItem(key);
  if (v !== 'A' && v !== 'B') {
    v = Math.random() < 0.5 ? 'A' : 'B';
    localStorage.setItem(key, v);
  }
  return v;
};

export const PromoModal = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [promo, setPromo] = useState(null);
  const [variant, setVariant] = useState('A');
  const shown = useRef(false);
  const lang = i18n.language?.split('-')[0]?.split('@')[0] || 'en';

  useEffect(() => {
    if (shown.current) return;
    const isExcluded = EXCLUDED_PATHS.some(p => location.pathname.includes(p));
    if (isExcluded) return;

    // If user arrived with ?promo=... in URL (e.g. from a Google Ad), they already
    // have a promo intent. Showing a different promo modal cannibalises that and
    // creates confusion. Skip popup entirely in that case.
    const urlPromo = new URLSearchParams(location.search).get('promo');
    if (urlPromo) return;

    const timer = setTimeout(() => {
      get('/api/promotions/active')
        .then(data => {
          if (data && data.active && data.title_en && data.image_url) {
            // Check if user already dismissed this specific promo
            const dismissedKey = `promo_dismissed_${data.promo_id}`;
            const dismissedAt = localStorage.getItem(dismissedKey);
            if (dismissedAt) {
              // Don't show again for 7 days after dismissal
              const dismissedTime = parseInt(dismissedAt, 10);
              if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) return;
            }
            // Assign A/B variant if test enabled and variant B copy exists for current language
            const hasVariantB = data.ab_test_enabled && data[`text_b_${lang}`];
            const v = hasVariantB ? getOrAssignVariant(data.promo_id) : 'A';
            setVariant(v);
            setPromo(data);
            shown.current = true;
            setTimeout(() => {
              setOpen(true);
              trackPromoModalView(data.promo_id, data.title_en, v);
            }, 1000);
          }
        })
        .catch(() => {});
    }, 4000);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search, lang]);

  const handleClose = () => {
    setOpen(false);
    if (promo) {
      localStorage.setItem(`promo_dismissed_${promo.promo_id}`, String(Date.now()));
      trackPromoModalClose(promo.promo_id, promo.title_en, variant);
    }
  };

  const handleCta = () => {
    setOpen(false);
    trackPromoModalClick(promo.promo_id, promo.title_en, promo.cta_link || '/book', variant);
    navigate(promo.cta_link || '/book');
  };

  if (!open || !promo) return null;

  // Belt-and-suspenders: if the user navigates into a booking/admin route
  // while the modal is already mounted, hide it without mutating state.
  // This prevents the popup from covering the calendar/checkout/payment UI.
  const isExcluded = EXCLUDED_PATHS.some(p => location.pathname.includes(p));
  if (isExcluded) return null;

  const title = promo[`title_${lang}`] || promo.title_en;
  // Variant B uses text_b_<lang>; Variant A uses text_<lang>
  const textKey = variant === 'B' ? `text_b_${lang}` : `text_${lang}`;
  const textFallbackKey = variant === 'B' ? 'text_b_en' : 'text_en';
  const text = promo[textKey] || promo[textFallbackKey] || promo.text_en;
  const cta = promo[`cta_${lang}`] || promo.cta_en;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="promo-modal-overlay" data-variant={variant}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white flex flex-col md:flex-row w-[92vw] max-w-[900px] max-h-[90vh] overflow-y-auto shadow-2xl" style={{ fontFamily: "'Open Sans', sans-serif" }} data-testid="promo-modal">
        {/* Image — top on mobile, left on desktop */}
        <div className="w-full md:w-[45%] flex-shrink-0 h-[200px] md:h-auto">
          <img src={promo.image_url} alt={title} className="w-full h-full object-cover" />
        </div>

        {/* Content */}
        <div className="flex-1 p-8 md:p-12 flex flex-col justify-center relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-5 text-stone-400 hover:text-stone-800 transition-colors text-2xl leading-none"
            data-testid="promo-modal-close"
          >&times;</button>

          <h2 className="text-3xl md:text-5xl font-bold text-stone-800 tracking-tight mb-5 md:mb-6" style={{ fontFamily: "'PT Serif', Georgia, serif" }}>
            {title}
          </h2>

          <p className="text-stone-500 text-sm md:text-lg leading-relaxed mb-8 md:mb-10">
            {text}
          </p>

          <button
            onClick={handleCta}
            className="self-start text-stone-500 text-sm border-b-2 border-stone-300 pb-1 hover:text-stone-800 hover:border-stone-800 transition-colors"
            data-testid="promo-modal-cta"
          >
            {cta}
          </button>
        </div>
      </div>
    </div>
  );
};
